import React, { useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { getSafeValue } from '../../../utils/security';
import { sanitizeFleetIdentifier } from '../../../utils/FleetHelpers';
import { colors, fontSize } from '../../../global/variables';
import EVSelector from './EVSelector';
import {
  StyledEditor,
  StyledEditorBody,
  StyledGrid,
  StyledField,
  StyledLabel,
  StyledRequiredMark,
  StyledInput,
  StyledSelect,
  StyledFooter,
  StyledCancelButton,
  StyledSaveButton,
} from './FleetEditorStyled';

const { primaryTextColor, aqua } = colors;
const { smaller, normal } = fontSize;

const StyledCheckboxRow = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: normal,
  color: primaryTextColor,
  cursor: 'pointer',
  marginBottom: '0.9rem',
});

const StyledEVSectionTitle = styled.div({
  fontSize: smaller,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 700,
  color: aqua,
  marginBottom: '0.5rem',
});

const FleetProfileEditor = ({
  isNew,
  profileName,
  fleetType,
  isElectricVehicle,
  evProfile,
  oAuth,
  apiKey,
  vehicleAmount,
  fleetTypeOptions,
  canFleetTypeBeElectric,
  onSave,
  onCancel,
  labels: {
    profileNameLabel,
    profileTypeLabel,
    electricVehicleLabel,
    electricVehicleProfileSectionTitle,
    saveProfileLabel,
    cancelProfileLabel,
    profileNamePlaceholder,
  },
}) => {
  const [draftName, setDraftName] = useState(isNew ? '' : profileName);
  const [draftFleetType, setDraftFleetType] = useState(isNew ? 'car' : fleetType);
  const [draftIsElectricVehicle, setDraftIsElectricVehicle] = useState(
    isNew ? false : isElectricVehicle,
  );
  const [draftEvProfile, setDraftEvProfile] = useState(isNew ? null : evProfile);

  const onNameChange = useCallback(
    (e) => setDraftName(sanitizeFleetIdentifier(e.target.value)),
    [],
  );

  const onFleetTypeChange = useCallback(
    (e) => {
      const newFleetType = e.target.value;
      setDraftFleetType(newFleetType);
      if (draftIsElectricVehicle && !canFleetTypeBeElectric(newFleetType))
        setDraftIsElectricVehicle(false);
    },
    [draftIsElectricVehicle, canFleetTypeBeElectric],
  );

  const onElectricVehicleToggle = useCallback(
    (e) => setDraftIsElectricVehicle(e.target.checked),
    [],
  );

  const onEVProfileChange = useCallback(
    (data) => setDraftEvProfile((prev) => (data ? { ...data, charging: prev?.charging } : null)),
    [],
  );

  const onChargingChange = useCallback(
    (chargingData) => setDraftEvProfile((prev) => ({ ...prev, charging: chargingData })),
    [],
  );

  const isValid = useMemo(
    () => (draftName || '').trim().length > 0 && !!draftFleetType,
    [draftName, draftFleetType],
  );

  const onSaveClick = useCallback(() => {
    onSave({
      name: draftName,
      fleetType: draftFleetType,
      isElectricVehicle: draftIsElectricVehicle,
      evProfile: draftIsElectricVehicle ? draftEvProfile : null,
    });
  }, [onSave, draftName, draftFleetType, draftIsElectricVehicle, draftEvProfile]);

  return (
    <StyledEditor>
      <StyledEditorBody>
        <StyledGrid>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-name">
              {profileNameLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-profile-name"
              type="text"
              placeholder={profileNamePlaceholder}
              value={draftName}
              onChange={onNameChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-type">
              {profileTypeLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledSelect
              id="input-fleet-profile-type"
              value={draftFleetType}
              onChange={onFleetTypeChange}
            >
              {Object.keys(fleetTypeOptions)
                .filter((typeID) => typeID !== 'electricVehicle')
                .map((typeID) => (
                  <option key={typeID} value={typeID}>
                    {getSafeValue(fleetTypeOptions, typeID)}
                  </option>
                ))}
            </StyledSelect>
          </StyledField>
        </StyledGrid>
        {canFleetTypeBeElectric(draftFleetType) && (
          <StyledCheckboxRow htmlFor="input-fleet-profile-ev">
            <input
              id="input-fleet-profile-ev"
              type="checkbox"
              checked={draftIsElectricVehicle}
              onChange={onElectricVehicleToggle}
            />
            {electricVehicleLabel}
          </StyledCheckboxRow>
        )}
        {draftIsElectricVehicle && (
          <>
            <StyledEVSectionTitle>{electricVehicleProfileSectionTitle}</StyledEVSectionTitle>
            <EVSelector
              oAuth={oAuth}
              apiKey={apiKey}
              onEVProfileChange={onEVProfileChange}
              initialProfile={draftEvProfile}
              charging={draftEvProfile?.charging}
              vehicleAmount={vehicleAmount}
              onChargingChange={onChargingChange}
            />
          </>
        )}
      </StyledEditorBody>
      <StyledFooter>
        <StyledCancelButton id="button-fleet-profile-cancel" type="button" onClick={onCancel}>
          {cancelProfileLabel}
        </StyledCancelButton>
        <StyledSaveButton
          id="button-fleet-profile-save"
          type="button"
          disabled={!isValid}
          onClick={onSaveClick}
        >
          {saveProfileLabel}
        </StyledSaveButton>
      </StyledFooter>
    </StyledEditor>
  );
};

export default FleetProfileEditor;
